import type { FragmentType } from '~/graphql/generated/fragment-masking';
import type { CurriculumTagListItem } from '~/types/Learn/curriculum';

import { adminCurriculumTagFieldsFragment, curriculumTagFieldsFragment } from '~/graphql/documents/learn/curriculumFrameworks';
import { useFragment } from '~/graphql/generated/fragment-masking';

/**
 * Unmasks an asset's `curriculumTags` into the plain shape the tag cell and the chips read.
 *
 * Every asset document selects the tags through `CurriculumTagFields`, so each one hands back
 * masked refs. Unmasking lives here rather than at each call site so the fragment stays the single
 * place the selection is written, and adding a field the cell needs is still one edit.
 *
 * The server labels the chain of levels on the tag (`CurriculumTag.ancestors`); the chip reads it
 * on the node, beside the node's own name and description, because that is where the lesson
 * roll-up already carries it. This is the one place the two shapes meet.
 */
const liftAncestorsOntoNode = <T extends CurriculumTagListItem & { ancestors: ReadonlyArray<string> }>(
  tag: T,
): CurriculumTagListItem => ({
  id: tag.id,
  node: { ...tag.node, ancestors: [...tag.ancestors] },
  framework: tag.framework,
  frameworkToneIndex: tag.frameworkToneIndex,
});

export const toCurriculumTagList = (
  tags?: ReadonlyArray<FragmentType<typeof curriculumTagFieldsFragment>> | null,
): CurriculumTagListItem[] => (tags ? useFragment(curriculumTagFieldsFragment, tags).map(liftAncestorsOntoNode) : []);

/** The same unmasking for the admin projection's tags, which the content pages read. */
export const toAdminCurriculumTagList = (
  tags?: ReadonlyArray<FragmentType<typeof adminCurriculumTagFieldsFragment>> | null,
): CurriculumTagListItem[] => (tags ? useFragment(adminCurriculumTagFieldsFragment, tags).map(liftAncestorsOntoNode) : []);
